/**
 * Vibecheck Verification System
 * 
 * Adds provenance tracking to AI-generated code:
 * - Marks verified code with "Verified by Vibecheck" badge
 * - Tracks which context folders/files were used
 * - Generates verification metadata for audit trails
 * - Embeds verification comments in generated code
 */

import * as fs from 'fs';
import * as path from 'path';
import * as crypto from 'crypto';

export interface VerificationMetadata {
  id: string;
  timestamp: string;
  verified: boolean;
  confidenceScore: number;
  confidenceGrade: string;
  contextUsed: ContextSource[];
  issues: string[];
  checksum: string;
}

export interface ContextSource {
  path: string;
  type: 'file' | 'folder';
  relevance: number;
  linesUsed?: { start: number; end: number }[];
}

export interface VerificationBadge {
  comment: string;
  metadata: VerificationMetadata;
}

export interface VerificationOptions {
  addComment: boolean;
  commentStyle: 'block' | 'line' | 'jsdoc';
  includeTimestamp: boolean;
  includeChecksum: boolean;
  includeContextPaths: boolean;
  includeConfidence: boolean;
  minConfidenceForBadge: number;
}

const DEFAULT_OPTIONS: VerificationOptions = {
  addComment: true,
  commentStyle: 'block',
  includeTimestamp: true,
  includeChecksum: true,
  includeContextPaths: true,
  includeConfidence: true,
  minConfidenceForBadge: 60,
};

export class VibecheckVerifier {
  private workspaceRoot: string;
  private verificationLog: VerificationMetadata[] = [];
  private logPath: string;

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.logPath = path.join(workspaceRoot, '.guardrail', 'verification-log.json');
    this.loadLog();
  }

  /**
   * Generate a verification badge for code
   */
  generateBadge(
    code: string,
    confidenceScore: number,
    confidenceGrade: string,
    contextUsed: ContextSource[],
    issues: string[],
    options: Partial<VerificationOptions> = {}
  ): VerificationBadge {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    const metadata = this.createMetadata(
      code,
      confidenceScore,
      confidenceGrade,
      contextUsed,
      issues
    );

    // Only add badge if confidence meets threshold
    const verified = confidenceScore >= opts.minConfidenceForBadge;
    metadata.verified = verified;

    const comment = this.formatComment(metadata, opts);

    // Log the verification
    this.verificationLog.push(metadata);
    this.saveLog();

    return { comment, metadata };
  }

  /**
   * Add verification badge to code
   */
  addBadgeToCode(
    code: string,
    badge: VerificationBadge,
    language: string
  ): string {
    if (!badge.metadata.verified) {
      // Don't add badge if not verified
      return code;
    }

    const commentedBadge = this.wrapInComment(badge.comment, language);
    
    // Find the right insertion point (after any shebang or initial comments)
    const lines = code.split('\n');
    let insertIndex = 0;

    // Skip shebang
    if (lines[0]?.startsWith('#!')) {
      insertIndex = 1;
    }

    // Skip initial block comment if present
    if (lines[insertIndex]?.trim().startsWith('/*')) {
      while (insertIndex < lines.length && !lines[insertIndex].includes('*/')) {
        insertIndex++;
      }
      insertIndex++; // Move past the closing */
    }

    // Insert the badge
    lines.splice(insertIndex, 0, commentedBadge, '');
    
    return lines.join('\n');
  }

  /**
   * Check if code already has a Vibecheck badge
   */
  hasVibecheckBadge(code: string): boolean {
    return code.includes('Verified by Vibecheck') || 
           code.includes('@vibecheck-verified') ||
           code.includes('VIBECHECK_ID:');
  }

  /**
   * Extract verification metadata from code with badge
   */
  extractBadgeMetadata(code: string): VerificationMetadata | null {
    const idMatch = code.match(/VIBECHECK_ID:\s*([a-f0-9-]+)/);
    if (!idMatch) return null;

    const id = idMatch[1];
    return this.verificationLog.find(v => v.id === id) || null;
  }

  /**
   * Verify that code matches its checksum
   */
  verifyChecksum(code: string): { valid: boolean; metadata?: VerificationMetadata } {
    const metadata = this.extractBadgeMetadata(code);
    if (!metadata) {
      return { valid: false };
    }

    // Remove the badge comment to get original code
    const codeWithoutBadge = this.removeBadge(code);
    const currentChecksum = this.generateChecksum(codeWithoutBadge);

    return {
      valid: currentChecksum === metadata.checksum,
      metadata,
    };
  }

  /**
   * Generate verification report for a file
   */
  generateReport(filePath: string): {
    hasVerification: boolean;
    metadata?: VerificationMetadata;
    checksumValid?: boolean;
    contextStillExists?: boolean;
  } {
    const fullPath = path.isAbsolute(filePath) 
      ? filePath 
      : path.join(this.workspaceRoot, filePath);

    if (!fs.existsSync(fullPath)) {
      return { hasVerification: false };
    }

    const code = fs.readFileSync(fullPath, 'utf-8');
    
    if (!this.hasVibecheckBadge(code)) {
      return { hasVerification: false };
    }

    const { valid, metadata } = this.verifyChecksum(code);
    
    // Check if context sources still exist
    let contextStillExists = true;
    if (metadata?.contextUsed) {
      for (const ctx of metadata.contextUsed) {
        const ctxPath = path.join(this.workspaceRoot, ctx.path);
        if (!fs.existsSync(ctxPath)) {
          contextStillExists = false;
          break;
        }
      }
    }

    return {
      hasVerification: true,
      metadata,
      checksumValid: valid,
      contextStillExists,
    };
  }

  /**
   * Get all verified files in workspace
   */
  async getVerifiedFiles(): Promise<Array<{
    path: string;
    metadata: VerificationMetadata;
    status: 'valid' | 'modified' | 'context_missing';
  }>> {
    const results: Array<{
      path: string;
      metadata: VerificationMetadata;
      status: 'valid' | 'modified' | 'context_missing';
    }> = [];

    for (const verification of this.verificationLog) {
      // Find the file that contains this verification
      // This is a simplified version - in practice you'd track file paths
      const status = 'valid'; // Would need to verify each file
      results.push({
        path: verification.contextUsed[0]?.path || 'unknown',
        metadata: verification,
        status,
      });
    }

    return results;
  }

  /**
   * Create verification metadata
   */
  private createMetadata(
    code: string,
    confidenceScore: number,
    confidenceGrade: string,
    contextUsed: ContextSource[],
    issues: string[]
  ): VerificationMetadata {
    return {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      verified: false, // Will be set based on confidence
      confidenceScore,
      confidenceGrade,
      contextUsed,
      issues,
      checksum: this.generateChecksum(code),
    };
  }

  /**
   * Format the verification comment
   */
  private formatComment(
    metadata: VerificationMetadata,
    options: VerificationOptions
  ): string {
    const lines: string[] = [];

    if (metadata.verified) {
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('  ✓ Verified by Vibecheck');
      lines.push('═══════════════════════════════════════════════════════════════');
    } else {
      lines.push('═══════════════════════════════════════════════════════════════');
      lines.push('  ⚠ Vibecheck: Review Recommended');
      lines.push('═══════════════════════════════════════════════════════════════');
    }

    lines.push('');
    lines.push(`  VIBECHECK_ID: ${metadata.id}`);

    if (options.includeConfidence) {
      lines.push(`  Confidence: ${metadata.confidenceScore}% (Grade: ${metadata.confidenceGrade})`);
    }

    if (options.includeTimestamp) {
      lines.push(`  Verified: ${metadata.timestamp}`);
    }

    if (options.includeContextPaths && metadata.contextUsed.length > 0) {
      lines.push('');
      lines.push('  Context Used:');
      for (const ctx of metadata.contextUsed.slice(0, 5)) {
        const icon = ctx.type === 'folder' ? '📁' : '📄';
        lines.push(`    ${icon} ${ctx.path} (${Math.round(ctx.relevance * 100)}% relevant)`);
      }
      if (metadata.contextUsed.length > 5) {
        lines.push(`    ... and ${metadata.contextUsed.length - 5} more`);
      }
    }

    if (metadata.issues.length > 0) {
      lines.push('');
      lines.push('  Issues Found:');
      for (const issue of metadata.issues.slice(0, 3)) {
        lines.push(`    ⚠ ${issue}`);
      }
    }

    if (options.includeChecksum) {
      lines.push('');
      lines.push(`  Checksum: ${metadata.checksum.substring(0, 16)}...`);
    }

    lines.push('');
    lines.push('═══════════════════════════════════════════════════════════════');

    return lines.join('\n');
  }

  /**
   * Wrap comment in language-appropriate comment syntax
   */
  private wrapInComment(content: string, language: string): string {
    const jsLangs = ['javascript', 'typescript', 'jsx', 'tsx', 'js', 'ts'];
    const pyLangs = ['python', 'py'];
    const htmlLangs = ['html', 'xml', 'svg'];
    const cssLangs = ['css', 'scss', 'sass', 'less'];
    const shellLangs = ['bash', 'sh', 'shell', 'zsh'];

    if (jsLangs.includes(language.toLowerCase())) {
      return `/**\n${content.split('\n').map(l => ` * ${l}`).join('\n')}\n */`;
    }
    
    if (pyLangs.includes(language.toLowerCase())) {
      return `"""\n${content}\n"""`;
    }

    if (htmlLangs.includes(language.toLowerCase())) {
      return `<!--\n${content}\n-->`;
    }

    if (cssLangs.includes(language.toLowerCase())) {
      return `/*\n${content}\n*/`;
    }

    if (shellLangs.includes(language.toLowerCase())) {
      return content.split('\n').map(l => `# ${l}`).join('\n');
    }

    // Default to block comment
    return `/*\n${content}\n*/`;
  }

  /**
   * Remove Vibecheck badge from code
   */
  private removeBadge(code: string): string {
    // Remove block comment badge
    const blockPattern = /\/\*\*[\s\S]*?Verified by Vibecheck[\s\S]*?\*\/\n*/;
    let cleaned = code.replace(blockPattern, '');

    // Remove Python docstring badge
    const pyPattern = /"""[\s\S]*?Verified by Vibecheck[\s\S]*?"""\n*/;
    cleaned = cleaned.replace(pyPattern, '');

    // Remove HTML comment badge
    const htmlPattern = /<!--[\s\S]*?Verified by Vibecheck[\s\S]*?-->\n*/;
    cleaned = cleaned.replace(htmlPattern, '');

    return cleaned;
  }

  /**
   * Generate unique verification ID
   */
  private generateId(): string {
    return `vc-${Date.now().toString(36)}-${crypto.randomBytes(4).toString('hex')}`;
  }

  /**
   * Generate checksum for code
   */
  private generateChecksum(code: string): string {
    return crypto.createHash('sha256').update(code).digest('hex');
  }

  /**
   * Load verification log from disk
   */
  private loadLog(): void {
    try {
      if (fs.existsSync(this.logPath)) {
        const data = JSON.parse(fs.readFileSync(this.logPath, 'utf-8'));
        this.verificationLog = data.verifications || [];
      }
    } catch (e) {
      console.error('Failed to load verification log:', e);
    }
  }

  /**
   * Save verification log to disk
   */
  private saveLog(): void {
    try {
      const dir = path.dirname(this.logPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }
      
      // Keep only last 1000 verifications
      const recentVerifications = this.verificationLog.slice(-1000);
      
      fs.writeFileSync(this.logPath, JSON.stringify({
        verifications: recentVerifications,
        lastUpdated: new Date().toISOString(),
      }, null, 2));
    } catch (e) {
      console.error('Failed to save verification log:', e);
    }
  }

  /**
   * Get verification statistics
   */
  getStats(): {
    totalVerifications: number;
    verified: number;
    needsReview: number;
    avgConfidence: number;
    topContextSources: Array<{ path: string; count: number }>;
  } {
    const verified = this.verificationLog.filter(v => v.verified).length;
    const needsReview = this.verificationLog.filter(v => !v.verified).length;
    
    const avgConfidence = this.verificationLog.length > 0
      ? this.verificationLog.reduce((sum, v) => sum + v.confidenceScore, 0) / this.verificationLog.length
      : 0;

    // Count context source usage
    const contextCounts = new Map<string, number>();
    for (const v of this.verificationLog) {
      for (const ctx of v.contextUsed) {
        contextCounts.set(ctx.path, (contextCounts.get(ctx.path) || 0) + 1);
      }
    }

    const topContextSources = Array.from(contextCounts.entries())
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalVerifications: this.verificationLog.length,
      verified,
      needsReview,
      avgConfidence: Math.round(avgConfidence),
      topContextSources,
    };
  }

  /**
   * Clear all verification history
   */
  clearHistory(): void {
    this.verificationLog = [];
    this.saveLog();
  }
}

/**
 * Generate a simple Vibecheck badge comment for quick use
 */
export function generateQuickBadge(
  language: string,
  confidenceScore: number,
  contextPaths: string[]
): string {
  const verifier = new VibecheckVerifier(process.cwd());
  
  const contextUsed: ContextSource[] = contextPaths.map(p => ({
    path: p,
    type: fs.existsSync(p) && fs.statSync(p).isDirectory() ? 'folder' : 'file',
    relevance: 1.0,
  }));

  const grade = confidenceScore >= 90 ? 'A' : 
                confidenceScore >= 80 ? 'B' : 
                confidenceScore >= 70 ? 'C' : 
                confidenceScore >= 60 ? 'D' : 'F';

  const badge = verifier.generateBadge(
    '', // Code not needed for badge generation
    confidenceScore,
    grade,
    contextUsed,
    []
  );

  return verifier['wrapInComment'](badge.comment, language);
}
